import React, { useState, useContext, useEffect } from "react";
import { FaMobile, FaTabletAlt, FaDesktop, FaSearch } from "react-icons/fa";
import { FaRegEye } from "react-icons/fa6";
import DataTable from "react-data-table-component";
import { AppContext } from "../Context/Provider";

const escapeCsvCell = (value) => {
  let text = String(value ?? "");
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

const VisitorTab = () => {
  const [filterText, setFilterText] = useState("");
  const {
    FetchVisitorsSummary,
    VisitorsSummaryData,
    FetchVisitorsData,
    VisitorsData,
  } = useContext(AppContext);

  // Fetch visitors summary data on component mount
  useEffect(() => {
    FetchVisitorsSummary();
    FetchVisitorsData();
  }, []);

  // KPI Data Array - Updated to use API data with proper colors and icons
  const kpiData = [
    {
      id: 1,
      title: "Today Visitors",
      value: VisitorsSummaryData?.todayVisitors || "0",
      // percentage: "+11.01%", // This could be calculated from API data if available
      isPositive: true,
      bgColor: "#F0F9FF", // Light blue
      borderColor: "#BAE6FD",
    },
    {
      id: 2,
      title: "Visitors This Week",
      value: VisitorsSummaryData?.weekVisitors || "0",
      // percentage: "-0.03%", // This could be calculated from API data if available
      isPositive: false,
      bgColor: "#F0FDF4", // Light green
      borderColor: "#BBF7D0",
    },
    {
      id: 3,
      title: "Visitors This Month",
      value: VisitorsSummaryData?.monthVisitors || "0",
      // percentage: "+15.03%", // This could be calculated from API data if available
      isPositive: true,
      bgColor: "#F0FDFA", // Soft teal/mint
      borderColor: "#5EEAD4",
    },
    {
      id: 4,
      title: "Total Visitors",
      value: VisitorsSummaryData?.totalVisitors || "0",
      // percentage: "+6.08%", // This could be calculated from API data if available
      isPositive: true,
      bgColor: "#FDF2F8", // Soft pink
      borderColor: "#F472B6",
    },
  ];

  // Visitor data from API
  const visitorData = VisitorsData || [];

  // Filter data based on search text
  const filteredData = visitorData.filter(
    (item) =>
      (item.ip || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.city || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.region || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.country || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.deviceType || "")
        .toLowerCase()
        .includes(filterText.toLowerCase()) ||
      (item.page || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.count || 0).toString().includes(filterText) ||
      (item.createdAt || "").toLowerCase().includes(filterText.toLowerCase())
  );

  // Clear search function
  const clearSearch = () => {
    setFilterText("");
  };

  // Export a CSV file that opens directly in Excel.
  const exportToExcel = () => {
    // Prepare data for export
    const exportData = filteredData.map((item, index) => ({
      "IP Address": item.ip || "N/A",
      City: item.city || "N/A",
      Region: item.region || "N/A",
      Country: item.country || "N/A",
      "Device Type": item.deviceType || "N/A",
      Page: item.page || "N/A",
      Visits: item.count || 0,
      Date: item.createdAt ? new Date(item.createdAt).toLocaleString() : "N/A",
    }));

    const headers = Object.keys(exportData[0] || {
      "IP Address": "",
      City: "",
      Region: "",
      Country: "",
      "Device Type": "",
      Page: "",
      Visits: "",
      Date: "",
    });
    const rows = exportData.map((row) =>
      headers.map((header) => escapeCsvCell(row[header])).join(","),
    );
    const csv = `\uFEFF${headers.map(escapeCsvCell).join(",")}\r\n${rows.join("\r\n")}`;

    // Generate filename with current date
    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `visitor_data_${currentDate}.csv`;
    const downloadUrl = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(downloadUrl);
  };

  // Visitor table columns
  const visitorColumns = [
    {
      name: "IP ADDRESS",
      selector: (row) => row.ip || "N/A",
      sortable: true,
      grow: 1,
      minWidth: "140px",
    },
    {
      name: "LOCATION",
      selector: (row) =>
        `${row.city || ""}, ${row.region || ""}, ${row.country || ""}`
          .replace(/^,\s*/, "")
          .replace(/,\s*,/g, ",") || "N/A",
      sortable: true,
      grow: 2,
      minWidth: "180px",
    },
    {
      name: "DEVICE",
      selector: (row) => row.deviceType || "N/A",
      sortable: true,
      grow: 1,
      minWidth: "120px",
      cell: (row) => {
        const deviceType = (row.deviceType || "").toLowerCase();
        let IconComponent = FaDesktop; // Default to desktop

        if (deviceType.includes("mobile")) {
          IconComponent = FaMobile;
        } else if (deviceType.includes("tablet")) {
          IconComponent = FaTabletAlt;
        }

        return (
          <div className="flex items-center gap-2">
            <IconComponent className="text-gray-600" />
            <span className="capitalize">{row.deviceType || "N/A"}</span>
          </div>
        );
      },
    },
    {
      name: "PAGE",
      selector: (row) => row.page || "N/A",
      sortable: true,
      grow: 2,
      minWidth: "200px",
    },
    {
      name: "VISITS",
      selector: (row) => row.count || 0,
      sortable: true,
      grow: 0.5,
      minWidth: "100px",
    },
    {
      name: "DATE",
      selector: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleString() : "N/A",
      sortable: true,
      grow: 1.5,
      minWidth: "160px",
    },
  ];

  // Custom styles for DataTable
  const customStyles = {
    table: {
      style: {
        width: "100%",
        minWidth: "100%",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#f9fafb",
        borderBottom: "1px solid #e5e7eb",
        padding: "12px 16px",
        fontSize: "0.75rem",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "#374151",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",
      },
    },
    cells: {
      style: {
        padding: "16px 16px",
        fontSize: "0.875rem",
        color: "#374151",
      },
    },
  };

  return (
    <>
      {/* KPI Cards - Updated to match Figma exactly */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {kpiData.map((kpi) => (
          <div
            key={kpi.id}
            className="rounded-xl shadow-sm p-6 border"
            style={{
              backgroundColor: kpi.bgColor,
              borderColor: kpi.borderColor,
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-2">
                  {kpi.title}
                </p>
                <p className="text-3xl font-bold text-gray-900">{kpi.value}</p>
              </div>
              <div className="flex items-center">
                <FaRegEye className="w-6 h-6 mr-1 text-green-600" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Search visitors..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          {filterText ? (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          ) : (
            <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          )}
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
        {/* Export Excel Button */}
        <div className="flex justify-end p-4 border-b border-gray-200">
          <button
            onClick={exportToExcel}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition-colors duration-200 flex items-center gap-2"
            disabled={!filteredData || filteredData.length === 0}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Export Excel
          </button>
        </div>

        {VisitorsData && VisitorsData.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">No visitor data available</p>
          </div>
        ) : VisitorsData ? (
          <DataTable
            columns={visitorColumns}
            data={filteredData}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50]}
            customStyles={customStyles}
            defaultSortField="ip"
            defaultSortAsc={true}
            subHeader
            subHeaderComponent={
              <div className="text-sm text-gray-600">
                {filterText &&
                  `Found ${filteredData.length} results for "${filterText}"`}
              </div>
            }
            responsive
            fixedHeader
          />
        ) : (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading visitor data...</p>
          </div>
        )}
      </div>
    </>
  );
};

export default VisitorTab;
